/**
 * Live pages that have changed since we last looked at them.
 *
 * This was drawn in seven weights of amber: the border, a tile behind the
 * icon, the headline, the trailing count, the body text, every site chip and
 * the "Review all" link. When everything in a banner is the alarm colour,
 * nothing in it can be more urgent than anything else, and the one part a
 * person is meant to click reads exactly like the sentence above it.
 *
 * It is a notice. The attention colour is spent once, on the mark that says
 * this is a notice, and the sites are the only things in it that go anywhere.
 *
 * It stays dismissible for the session only. Nobody should be permanently
 * blinded to a page that changed under them.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { X } from 'lucide-react'
import { cn } from '@/utils/cn'
import { Button, Notice } from '@/design-system/primitives'
import { Chip } from '@/design-system/data'
import '@/design-system/dashboard.css'
import '@/design-system/data.css'

const DISMISS_KEY = 'dashboard_drift_summary_dismissed'
const MAX_VISIBLE_CHIPS = 6

export interface AffectedDomain {
  domain: string
  displayName?: string | null
  openDriftEvents?: number
}

interface DashboardDriftSummaryProps {
  affectedDomains: AffectedDomain[]
  onReviewAll: () => void
  className?: string
}

export function DashboardDriftSummary({
  affectedDomains,
  onReviewAll,
  className,
}: DashboardDriftSummaryProps) {
  const navigate = useNavigate()
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    return sessionStorage.getItem(DISMISS_KEY) === '1'
  })

  if (dismissed || affectedDomains.length === 0) return null

  const totalDrift = affectedDomains.reduce((sum, d) => sum + (d.openDriftEvents ?? 0), 0)
  const visible = affectedDomains.slice(0, MAX_VISIBLE_CHIPS)
  const overflow = affectedDomains.length - visible.length
  const domainCount = affectedDomains.length

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1')
    } catch {
      // sessionStorage unavailable, so the dismissal lasts this render only.
    }
    setDismissed(true)
  }

  return (
    <div className={cn('ds-dash__drift', className)}>
      <Notice kind="look">
        <b>
          {domainCount === 1
            ? 'One site has pages that changed'
            : `${domainCount} sites have pages that changed`}
          {totalDrift > domainCount && `, ${totalDrift} pages in all`}
        </b>
        <p>
          These pages are not what they were when we last checked them. Look at what changed,
          then either fix it or tell us the new version is the right one.
        </p>
        <div className="ds-dash__driftsites">
          {visible.map((d) => (
            <Chip
              key={d.domain}
              count={(d.openDriftEvents ?? 0) > 1 ? d.openDriftEvents : undefined}
              onClick={() => navigate(`/domains/${encodeURIComponent(d.domain)}/monitoring`)}
            >
              {d.displayName || d.domain}
            </Chip>
          ))}
          {overflow > 0 && <span className="ds-dash__driftmore">and {overflow} more</span>}
          <Button variant="ghost" size="sm" onClick={onReviewAll}>
            Show me all of them
          </Button>
        </div>
      </Notice>
      <button
        type="button"
        onClick={handleDismiss}
        className="ds-dash__driftclose"
        aria-label="Hide this until next time"
        title="Hide this until next time"
      >
        <X aria-hidden="true" />
      </button>
    </div>
  )
}

export default DashboardDriftSummary
